import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {
  Backdrop,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {QueryVehicleInventory, updateVehicleInventory, useQueryVehicleInventory} from "../../../Data/VehicleData";
import {AppState} from '../../../store/rootReducer';
import {ApplicationDispatch,} from "../../../store/application/types";
import {IApplication} from "../../../Interfaces/Application/IApplication";
import Alert from '@mui/material/Alert';
import LockNotice from '../../Shared/LockNotice';
import {isEmpty, isNil} from 'ramda';
import {
  dateDiffStringAsTotalTime,
  nullOrPositiveNumberString,
  userHasPermission
} from "../../../utils/utils";
import {useConfiguration} from "../../../Data/ConfigurationData";
import {handleVehicleUpdate} from "./OrderEditorTabs";
import {useBusinessRulesEngine} from "../../../Data/BusinessRulesData";
import {Constants, permissions} from "../../../utils/constants";
import {useEmployeeRolePermissions} from "../../../Data/EmployeeRoleData";
import VehicleEditIconComponent from "../../VehicleEditIconComponent";
import VehicleAddIconComponent from "../../VehicleAddIconComponent";
import {
  addThousandsSeparator,
  ApplicationQuoteType, usesFinanceQuote,
  hasValue,
  LoadingPanel,
  LookupItem,
  PermissionSet,
  toDollars,
  Vehicle
} from "@amsanalytics/react-shared-library";
import {Organization} from "@amsanalytics/react-shared-library/build/Models/Organization";
import {useFinanceQuoteSolve} from "../../../hooks/UseFinanceQuoteSolve";
import {useLeaseQuoteSolve} from "../../../hooks/UseLeaseQuoteSolve";
import {usePostedAppLock} from "../../../hooks/usePostedAppLock";

interface IVehicleSelectProps {

}

const VehicleSelect: React.FC<IVehicleSelectProps> = (props) => {
  const styles = {
    root: {
      flexGrow: 1,
      padding: 24,
    },
    paper: {
      padding: 16,
      textAlign: 'center',
      color: '#000',
    },
    expand: {
      transform: 'rotate(0deg)',
      transition: 'transform 0.2s ease-in-out',
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
  };
  const [canView, canEdit, canAct, canActInStatus] = useEmployeeRolePermissions();
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();
  const [updatingVehicleInventory, setUpdatingVehicleInventory] = useState(
    false
  );
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  // const vehicles: Vehicle[] = useSelector((state: AppState) => state.applications.vehicles);
  const organizations: Organization[] = useSelector((state: AppState) => state.applications.lookups.organizations);
  const bookoutSources: LookupItem[] = useSelector((state: AppState) => state.applications.lookups.bookoutSources);
  const currentApp: IApplication = useSelector(
    (state: AppState) => state.applications.editingApp!
  );
  // const dealerFlowData = useSelector((state: AppState) => state.applications.dealertrackDealerDecisionData);

  const { data: configuration } = useConfiguration();

  const [stockNumber, setStockNumber] = useState<string>("");
  const [vinNumber, setVinNumber] = useState<string>("");
  const [locationId, setLocationId] = useState<string>(configuration?.featureFlags?.[FeatureFlagIds.DefaultVehicleLocation] ? currentApp.organizationId : "");
  const [yearFrom, setYearFrom] = useState<string>("");
  const [yearTo, setYearTo] = useState<string>("");
  const [make, setMake] = useState<string>("");
  const [minMileage, setMinMileage] = useState<string>("");
  const [maxMileage, setMaxMileage] = useState<string>("");
  const [areMileagesValid, setAreMileagesValid] = useState<boolean>(true);
  const [arePricesValid, setArePricesValid] = useState<boolean>(true);
  const [model, setModel] = useState<string>("");
  const [priceFrom, setPriceFrom] = useState<string>("");
  const [priceTo, setPriceTo] = useState<string>("");
  const [vehicleToSelect, setVehicleToSelect] = useState<Vehicle | undefined>(undefined);
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser!.employee);
  const [handleQuoteSolve] = useFinanceQuoteSolve();
  const { solveWithNewVehicle, resolveAfterVehicleEdit } = useLeaseQuoteSolve();
  const { isSectionLocked, lockedTooltip, lockMessage } = usePostedAppLock();
  const vehicleLocked = isSectionLocked('Vehicle');
  const vehicleLockTooltip = lockedTooltip('Vehicle');

  const showCreateButton = !!(activeUser && userHasPermission(activeUser, permissions.vehicles, PermissionSet.C)
    && configuration?.featureFlags?.[FeatureFlagIds.AllowAddVehicleInLOS] === true && currentApp.organizationId);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const [
    queryVehicleInventory,
    setQueryVehicleInventory,
  ] = useState<QueryVehicleInventory>({
    locationId,
  });

  const { resolvedData: data } = useQueryVehicleInventory(
    queryVehicleInventory
  );

  useEffect(() => {
    if (data?.pageNumber != page) {
      setPage(data?.pageNumber || 0);
    }
  }, [data]);

  const getVehicle = (id: string) => {
    const vehicle = data?.vehicles?.find((x) => x.id === id);

    if (!vehicle) throw new Error("unable to find vehicle to add");

    return vehicle;
  };
  
  const handleVehicleUpdateBasedOnQuoteType = (vehicle: Vehicle) => {
    const aQuoteHasBeenCalculated = currentApp.financeQuotes.some(afq => hasValue(afq.quoteGeneratedDate));
    if (usesFinanceQuote(currentApp.quoteType) && aQuoteHasBeenCalculated) {
      // Divert the normal operation to run a quote calculation if the app is for a finance quote AND they have already have a quote calculation
      handleQuoteSolve(vehicle);
    } else {
      // A solved lease re-solves with the new vehicle (the server attaches it); otherwise the normal vehicle update
      const leaseResolved = currentApp.quoteType === ApplicationQuoteType.Lease && solveWithNewVehicle(currentApp, vehicle);
      if (!leaseResolved) {
        handleVehicleUpdate(currentApp, vehicle, configuration?.featureFlags?.[FeatureFlagIds.UseVehicleInQuoteByDefaultWhenVehicleChanges] === true, dispatch, businessRulesEngine);
      }
    }
  };
  
  const selectVehicle = (vehicleId: string) => {
    const vehicle = getVehicle(vehicleId);
    if (vehicle.organizationId !== currentApp?.organizationId) {
      setVehicleToSelect(vehicle);
    } else {
      handleVehicleUpdateBasedOnQuoteType(vehicle);
    }
  };

  const updateLocationId = (newLocationId: string) => {
    setLocationId(newLocationId);
  };

  // Handle cases where user enters value into either the min or max mileage inputs.
  const handleMileageChange = (mileage: string, isMin: boolean) => {
    isMin ? setMinMileage(mileage) : setMaxMileage(mileage);
    if (isMin) {
      setAreMileagesValid((isNil(maxMileage) || isNil(mileage) || isEmpty(maxMileage) || isEmpty(mileage)) ? true : +mileage <= +maxMileage);
    } else {
      setAreMileagesValid((isNil(minMileage) || isNil(mileage) || isEmpty(minMileage) || isEmpty(mileage)) ? true : +minMileage <= +mileage);
    }
  };

  // Handle cases where user enters value into eithe the min or max mileage inputs.
  const handlePriceChange = (price: string, isMin: boolean) => {
    isMin ? setPriceFrom(price) : setPriceTo(price);
    if (isMin) {
      setArePricesValid((isNil(priceTo) || isNil(price) || isEmpty(priceTo) || isEmpty(price)) ? true : +price <= +priceTo);
    } else {
      setArePricesValid((isNil(priceFrom) || isNil(price) || isEmpty(priceFrom) || isEmpty(price)) ? true : +priceFrom <= +price);
    }
  };

  useEffect(() => {
    const query: QueryVehicleInventory = {
      stockNumber: stockNumber.toUpperCase(),
      vinNumber: vinNumber.toUpperCase(),
      locationId,
      yearFrom: parseInt(yearFrom, 10),
      yearTo: parseInt(yearTo, 10),
      make: make.toUpperCase(),
      minMileage: parseInt(minMileage, 10),
      maxMileage: parseInt(maxMileage, 10),
      model: model.toUpperCase(),
      priceFrom: parseInt(priceFrom, 10),
      priceTo: parseInt(priceTo, 10),
      pageNumber: page,
      pageSize: rowsPerPage,
    };
    if (
      locationId !== queryVehicleInventory.locationId ||
      page !== queryVehicleInventory.pageNumber ||
      rowsPerPage !== queryVehicleInventory.pageSize
    ) {
      // Call the server right away.
      setQueryVehicleInventory(query);
    } else {
      // Debounce the server call.
      const handler = setTimeout(() => {
        setQueryVehicleInventory(query);
      }, 500);
      return () => {
        clearTimeout(handler);
      };
    }
  }, [
    stockNumber,
    vinNumber,
    locationId,
    yearFrom,
    yearTo,
    make,
    minMileage,
    maxMileage,
    model,
    priceFrom,
    priceTo,
    page,
    rowsPerPage,
  ]);

  const [isAdvancedSearchExpanded, setIsAdvancedSearchExpanded] = useState(
    false
  );

  const canViewActualCashValueField = userHasPermission(activeUser, permissions.vehicleACV, PermissionSet.R);

  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: 'calc(100vh - 112px)', overflowY: 'auto', overflowX: 'hidden' }}>
      {vehicleLocked && <LockNotice message={lockMessage} />}
      <Grid style={styles.root} container spacing={1}>
        <Card>
          <CardHeader
            title={
              <Typography variant="h6" component="h6">
                Select Vehicle
              </Typography>
            }
            action={
              <IconButton
                onClick={() =>
                  setIsAdvancedSearchExpanded(!isAdvancedSearchExpanded)
                }
                aria-expanded={isAdvancedSearchExpanded}
                aria-label="advanced search options"
              >
                <Typography variant="subtitle1" component="h6">
                  Advanced Search
                </Typography>
                <ExpandMoreIcon/>
              </IconButton>
            }
          />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <TextField
                  value={stockNumber}
                  onChange={(e) => setStockNumber(e.currentTarget.value)}
                  fullWidth
                  label="Stock #"
                  id="stock-number"
                />
                <TextField
                  value={vinNumber}
                  onChange={(e) => setVinNumber(e.currentTarget.value)}
                  style={{ marginTop: "5px", fontWeight: 400, fontSize: "1rem" }}
                  fullWidth
                  label="VIN #"
                  id="vin-number"
                />
              </Grid>
              <Grid item xs={5}>
                <FormControl fullWidth>
                  <InputLabel shrink>Location</InputLabel>
                  <Select
                    name="location"
                    value={locationId}
                    onChange={(e) => updateLocationId(e.target.value as string)}
                    displayEmpty
                    variant='standard'
                  >
                    <MenuItem value={""}>All Locations</MenuItem>
                    {organizations.filter(o => o.typeId === Constants.organizationTypes.storeId).map((o, key) => (
                      <MenuItem key={key} value={o.id}>
                        {o.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {showCreateButton &&
                <Grid item xs={4}>
                  <Tooltip title={vehicleLockTooltip} placement="top">
                    <span>
                      <VehicleAddIconComponent
                        applicationId={currentApp.id}
                        vehicleOrganizationId={currentApp.organizationId}
                        disabled={vehicleLocked}
                      />
                    </span>
                  </Tooltip>
                </Grid>
              }
            </Grid>
          </CardContent>
          <CardActions disableSpacing />
          <Collapse in={isAdvancedSearchExpanded}>
            <CardContent>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={6}>
                  <TextField
                    value={yearFrom}
                    onChange={(e) => setYearFrom(nullOrPositiveNumberString(e.currentTarget.value))}
                    label="Year From"
                    type="number"
                    id="year-from"
                  />
                  <TextField
                    value={yearTo}
                    onChange={(e) => setYearTo(nullOrPositiveNumberString(e.currentTarget.value))}
                    label="Year To"
                    type="number"
                    id="year-to"
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    value={make}
                    onChange={(e) => setMake(e.currentTarget.value)}
                    fullWidth
                    label="Make"
                    id="make"
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    value={model}
                    onChange={(e) => setModel(e.currentTarget.value)}
                    fullWidth
                    label="Model"
                    id="model"
                  />
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    label="Min Mileage"
                    type="number"
                    onChange={(e) => handleMileageChange(nullOrPositiveNumberString(e.target.value), true)}
                  />
                  <TextField
                    label="Max Mileage"
                    type="number"
                    onChange={(e) => handleMileageChange(nullOrPositiveNumberString(e.target.value), false)}
                  />
                  {!areMileagesValid && (
                    <Alert severity="warning" >Min mileage should be less than or equal to max mileage.</Alert>
                  )}
                </Grid>
                <Grid item xs={6} sm={6}>
                  <TextField
                    value={priceFrom}
                    onChange={(e) => handlePriceChange(nullOrPositiveNumberString(e.currentTarget.value), true)}
                    label="Price From"
                    type="number"
                    id="price-from"
                  />
                  <TextField
                    value={priceTo}
                    onChange={(e) => handlePriceChange(nullOrPositiveNumberString(e.currentTarget.value), false)}
                    label="Price To"
                    type="number"
                    id="price-to"
                  />
                  {!arePricesValid && (
                    <Alert severity="warning" >Min price should be less than or equal to max price.</Alert>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Stock #</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Mileage</TableCell>
              <TableCell>VIN</TableCell>
              <TableCell>Selling Price</TableCell>
              {canViewActualCashValueField === true && <TableCell>ACV</TableCell>}
              {canViewActualCashValueField === true && <TableCell>Bookout</TableCell>}
              {canViewActualCashValueField === true && <TableCell>Bookout Source</TableCell>}
              <TableCell>Down Payment</TableCell>
              <TableCell>Time In Inventory</TableCell>
              <TableCell>Lot Date</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.vehicles
              ? data.vehicles.map((vehicle) => {
                const vehicleOrganization = organizations.find(o => o.id === vehicle.organizationId);
                return (
                  <TableRow key={vehicle.id.toString()}>
                    <TableCell>{vehicle.stockNumber}</TableCell>
                    <TableCell>
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </TableCell>
                    <TableCell>{addThousandsSeparator(vehicle.mileage)}</TableCell>
                    <TableCell>{vehicle.vin}</TableCell>
                    <TableCell>{toDollars(vehicle.carPrice)}</TableCell>
                    {canViewActualCashValueField === true && <TableCell>{toDollars(vehicle.acv)}</TableCell>}
                    {canViewActualCashValueField === true && <TableCell>{vehicle.bookoutValue ? toDollars(vehicle.bookoutValue) : ''}</TableCell>}
                    {canViewActualCashValueField === true && <TableCell>{vehicle.bookoutSourceId && vehicle.bookoutSourceId.toLowerCase() !== Constants.bookoutSources.other ? bookoutSources.find(bs => bs.id.toLowerCase() === vehicle.bookoutSourceId?.toLowerCase())?.name : vehicle.otherBookoutSource}</TableCell>}
                    <TableCell>{toDollars(vehicle.downPayment)}</TableCell>
                    <TableCell>{
                      vehicle.carInventoryDate &&
                      dateDiffStringAsTotalTime(
                        new Date().toUTCString(),
                        vehicle.carInventoryDate ? (vehicle.carInventoryDate as any as string) : ''
                      )}
                    </TableCell>
                    <TableCell>
                      {hasValue(vehicle.carLotDate)
                        ? new Date(vehicle.carLotDate!).toLocaleDateString()
                        : ""}
                    </TableCell>
                    <TableCell>{vehicleOrganization?.name}</TableCell>
                    <TableCell>
                      <Tooltip title={vehicleLockTooltip || "Add vehicle to application"}>
                        <span>
                          <IconButton disabled={vehicleLocked} onClick={() => selectVehicle(vehicle.id)}>
                            <AddIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {canEdit("Vehicle") &&
                        <Tooltip title={vehicleLockTooltip || "Edit Vehicle"} placement="top">
                          <span>
                            <VehicleEditIconComponent
                              disabled={vehicleLocked}
                              vehicleOnApp={currentApp.vehicle}
                              vehicleToEdit={vehicle}
                              organizations={organizations}
                              canEditFullVehicle={configuration?.featureFlags?.[FeatureFlagIds.AllowVehicleFullEdit] === true}
                              canEditMilage={activeUser && userHasPermission(activeUser, permissions.inventoryMileage, PermissionSet.U)}
                              applicationId={currentApp.id}
                              isIndirectApplication={(currentApp.quoteType === ApplicationQuoteType.Indirect)}
                              onVehicleSaved={(savedVehicle) => resolveAfterVehicleEdit(currentApp, savedVehicle)}
                            />
                          </span>
                        </Tooltip>
                      }
                    </TableCell>
                  </TableRow>
                );
              })
              : null}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                colSpan={8}
                rowsPerPage={rowsPerPage}
                count={data?.total || 0}
                page={page}
                SelectProps={{ native: true }}
                onPageChange={(e: any, p: any) => setPage(p)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableRow>
          </TableFooter>
        </Table>

        <Button
          variant="contained"
          style={{ margin: 10, height: "40px" }}
          onClick={async () => {
            try {
              setUpdatingVehicleInventory(true);
              await updateVehicleInventory(dispatch);
            } finally {
              setUpdatingVehicleInventory(false);
            }
          }}
        >
          Refresh Vehicle Inventory
        </Button>
      </Grid>
      <Backdrop open={updatingVehicleInventory}>
        <LoadingPanel label='' />
      </Backdrop>
      <Dialog
        open={!!vehicleToSelect}
        onClose={() => setVehicleToSelect(undefined)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Select vehicle from another location?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This vehicle is in a different location from the application.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setVehicleToSelect(undefined)}
            color="inherit"
            autoFocus
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => {
              handleVehicleUpdateBasedOnQuoteType(vehicleToSelect!);
              setVehicleToSelect(undefined);
            }}
          >
            Select
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default VehicleSelect;
